import React, {useEffect, useState, useRef} from 'react'
import {
    Flex,
    Box,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useParams} from 'react-router'
import {useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'

// PARTIALS
import Breadcrumb from '../breadcrumb'
import {RecipeHeroBanner} from '../editorial-components'
import IngredientsMenu from './partials/ingredients-menu'
import RecipeStepsMenu from './partials/recipe-steps-menu'
import RecipeProductSuggestions from './partials/recipe-product-suggestions'
import {useGetRecipeData} from './utils/use-get-recipe-info'
import LoadingSpinner from '../loading-spinner'
import {BRAND_LABEL} from '../../brand'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const RecipeDetails = () => {
    const [darkBgHeight, setDarkBgHeight] = useState(0)

    const ingredientsRef = useRef(null)
    const stepsRef = useRef(null)
    const {page, recipe_id} = useParams()
    const styles = useMultiStyleConfig('RecipeDetails', {})
    const {details} = styles || {}
    const intl = useIntl()
    const location = useLocation()
    const {customParams} = getConfig()
    const {locale} = useMultiSite()
    const {data, isLoading} = useGetRecipeData(recipe_id)
    const isColumn = useBreakpointValue({base: true, sm: true, md: true, lg: false})

    const {
        recipe,
        allIngredientsData = [],
        allUnitsData = [],
        productsUsedInRecipe = [],
        toolsUsedInRecipe = [],
    } = data || {}

    useEffect(() => {
        const handleResize = () => {
            // Recalculate on resize
            if (!isColumn) {
                if (ingredientsRef.current && stepsRef.current) {
                    const left = ingredientsRef.current.offsetHeight
                    const right = stepsRef.current.offsetHeight
                    setDarkBgHeight(Math.max(left, right))
                }
            } else if (ingredientsRef.current) {
                setDarkBgHeight(ingredientsRef.current.offsetHeight + 75)
            }
        }

        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [recipe, isColumn, productsUsedInRecipe, toolsUsedInRecipe])

    if (!recipe || isLoading) return <LoadingSpinner />

    const utensilsList = recipe?.products_utensils || {}

    // Build utensils list
    const allUtensils = [
        ...(utensilsList?.utensils?.map(
            (u) => `${u?.quantity || ''} ${u?.utensil?.[0]?.title || ''} ${u?.note || ''}`,
        ) || []),
        ...(utensilsList?.product_model?.map(
            (u) => `${u?.title || u?.description || ''} ${u?.note || ''}`,
        ) || []),
        ...(utensilsList?.tools?.map((u) => `${u?.description || ''} ${u?.note || ''}`) || []),
    ]

    // Breadcrumbs
    const breadcrumbCategories = [
        {
            id: 'home',
            name: intl.formatMessage({
                id: 'recipes.listing_page.breadcrumbs.return_home',
                defaultMessage: 'Return to homepage',
            }),
            c_slugUrl: `/${locale?.alias}`,
        },
        {
            id: 'recipes_page',
            name: intl.formatMessage({
                id: 'recipes.listing_page.breadcrumbs.recipes',
                defaultMessage: 'Recipes',
            }),
            c_slugUrl: `/r/${page}`,
        },
        {id: 'recipe_detail_page', name: recipe?.recipe_title, c_slugUrl: location.pathname},
    ]

    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const mainImage = recipe?.recipe_media?.recipe_image?.url
    const absolutePath_logoImage = 'https://dam.delonghi.com/600x600/assets/201985'
    const organizationHomepageURL = 'https://' + customParams.currentHost
    const brandName = `De'Longhi Appliances S.r.l`
    const recipeCategory = recipe?.recipe_classification?.recipe_category?.[0]?.title || ''
    const datePublished = recipe?.created_at?.slice(0, 10)
    const recipeDescription = recipe?.description || ''
    const recipeName = recipe?.recipe_title || recipe?.title || ''
    const recipeImageUrl = recipe?.recipe_media?.recipe_image?.url || ''

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: recipeName,
        description: recipeDescription,
        url: currentPath,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbCategories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        url: recipeImageUrl,
        creditText: brandName,
    }

    const getFullIngredientList = (ingredients, allIngredientsData, allUnitsData) => {
        if (!ingredients?.length) return []

        const result = []

        for (let ing of ingredients) {
            for (let mainIng of ing?.ingredient || []) {
                const ingredient = allIngredientsData.find(
                    (obj) => obj.uid === mainIng?.mainIngredient?.ingredientId,
                )
                const unit = allUnitsData.find((u) => u.uid === mainIng?.mainIngredient?.unitId)
                result.push(
                    `${mainIng?.mainIngredient?.quantity || ''} ${unit?.title || ''} ${
                        ingredient?.title || ''
                    }${
                        mainIng?.mainIngredient?.notes ? `, ${mainIng.mainIngredient.notes}` : ''
                    }`.trim(),
                )
            }
        }
        return result
    }

    const recipeIngredientsList = getFullIngredientList(
        recipe?.ingredients,
        allIngredientsData,
        allUnitsData,
    )

    const recipeInstructions =
        recipe?.recipe_steps?.map((step) => ({
            '@type': 'HowToStep',
            text: step.instructions
                ? step.instructions
                      .replace(/<[^>]+>/g, '')
                      .replace(/\s+/g, ' ')
                      .trim()
                : '',
        })) || []

    const recipeSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'Recipe',
        name: recipeName,
        image: mainImage,
        author: {
            '@type': 'Organization',
            name: brandName,
            logo: absolutePath_logoImage,
            url: organizationHomepageURL,
            brand: {
                '@type': 'Brand',
                name: BRAND_LABEL,
            },
        },
        datePublished,
        description: recipeDescription,
        recipeCategory,
        recipeIngredient: recipeIngredientsList,
        recipeInstructions,
    }

    const seo = recipe?.seo_standard || {}
    const seoMetaTitle = seo?.meta_title
    const seoMetaDescription = seo?.meta_description
    const seoSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: seoMetaTitle,
        description: seoMetaDescription,
        url: currentPath,
    }

    return (
        <Flex {...details?.detailsContainer}>
            <Helmet>
                {seoMetaTitle && <title>{seoMetaTitle}</title>}
                {seoMetaDescription && <meta name="description" content={seoMetaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(seoSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(recipeSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {/* Breadcrumbs */}
            <Flex {...details?.breadcrumbsContainer} layerStyle="page">
                <Breadcrumb categories={breadcrumbCategories} w="100%" h="100%" />
            </Flex>

            {/* Hero Section */}
            <Flex justifyContent="center" width="100%" bg="background.light-dark">
                <RecipeHeroBanner
                    title={recipe?.recipe_title}
                    description={recipe?.description}
                    mediaData={
                        recipe.recipe_media?.recipe_image ??
                        recipe.recipe_media?.recipe_video ??
                        null
                    }
                    recipeInfo={{
                        author: recipe?.author,
                        time: recipe?.recipe_time,
                        classification: recipe?.recipe_classification,
                    }}
                />
            </Flex>

            {/* Product Suggestions */}
            {productsUsedInRecipe.length > 0 && (
                <Flex width="100%" layerStyle="page">
                    <RecipeProductSuggestions
                        textBoxTitle={intl.formatMessage({
                            id: 'recipes.details_page.carousel.can_prepare_with.text',
                            defaultMessage: 'This recipe is prepared with:',
                        })}
                        madeWith={productsUsedInRecipe}
                        isMainProduct={true}
                        toolsGroup={toolsUsedInRecipe}
                    />
                </Flex>
            )}

            {/* Ingredients + Steps Section */}
            <Box position="relative" w="100%">
                {/* Background layer */}
                <Box
                    {...styles?.recipeInfoSudoContainer}
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        w: {base: '100%', lg: '50%'},
                        h: {base: `${darkBgHeight}px`, lg: '100%'},
                        bg: 'background.light-dark',
                    }}
                />

                {/* Foreground content */}
                <Flex
                    layerStyle="page"
                    {...details?.recipeInfoContainer}
                    position="relative"
                    zIndex={1}
                >
                    <Box
                        ref={ingredientsRef}
                        w={{base: '100%', lg: '50%'}}
                        position={{base: 'relative', lg: 'sticky'}}
                        top={{base: '0', lg: 0}}
                        alignSelf="flex-start"
                    >
                        <IngredientsMenu
                            ingredients={recipe?.ingredients}
                            servings={recipe?.makes_servings}
                            allUtensils={allUtensils}
                            allIngredients={allIngredientsData}
                            allUnits={allUnitsData}
                        />
                    </Box>
                    <Box ref={stepsRef} w={{base: '100%', lg: '50%'}}>
                        <RecipeStepsMenu recipeSteps={recipe?.recipe_steps} />
                    </Box>
                </Flex>
            </Box>

            {/* Additional product suggestions */}
            {productsUsedInRecipe.length > 1 && (
                <Flex width="100%" layerStyle="page">
                    <RecipeProductSuggestions
                        textBoxTitle={intl.formatMessage({
                            id: 'recipes.details_page.carousel.can_also_prepare_with.text',
                            defaultMessage: 'You can also prepare this recipe* with:',
                        })}
                        madeWith={productsUsedInRecipe}
                        isMainProduct={false}
                    />
                </Flex>
            )}
        </Flex>
    )
}

export default RecipeDetails
